import * as React from 'react'

import { observer } from 'mobx-react'

import Loading from 'modules/core/components/Loading'
import i18n from 'modules/core/lib/i18n'
import type { TentacleErrorData } from 'modules/core/lib/parseErrorData'

import BlankSlate from 'modules/reports/components/BlankSlate'
import ErrorBlankSlate from 'modules/reports/components/ErrorBlankSlate'
import type ReportForm from 'modules/reports/lib/ReportForm'
import type ReportStruct from 'modules/reports/lib/ReportStruct'
import MuncherFactory from 'modules/reports/lib/muncher/muncherFactory'
import type { ResourceData } from 'modules/reports/store/models/Resource'

import { ChartError } from './ChartError'
import Area from './types/Area'
import Bars from './types/Bars'
import Lines from './types/Lines'
import NumericMetric from './types/NumericMetric'
import Pie from './types/Pie'
import Scatterplot from './types/Scatterplot'
import Table from './types/Table'

interface Props {
  data?: ResourceData
  error?: TentacleErrorData | Error
  reportStruct: ReportStruct
  showLegend?: boolean
  reduceMargins?: boolean
  loading?: boolean
  reportForm?: ReportForm
  withinDashboardItem?: boolean
}

const TYPES: Record<string, React.ComponentType<any>> = {
  bars: Bars,
  pie: Pie,
  table: Table,
  lines: Lines,
  numeric_metric: NumericMetric,
  area: Area,
  scatterplot: Scatterplot,
}

const EmptyResult = () => {
  return (
    <BlankSlate
      message={i18n.t('custom_reports.editor.no_results')}
      illustration="task-list-search"
    />
  )
}
/**
 * Chart is the component that reads the report struct type and the report
 * data structure structure in order to instantiate a chart.
 *
 * Supported chart type: Area, Bars, Lines, Numeric metric, Pie, Scatterplot, Table.
 * Chart components throws an exception when there is not a report struct type that matched with the available charts.
 *
 * This component have three components nested to this one: Loading, ErrorBlankState, EmptyResult and ChartType.
 *
 * Loading is a generic component to display a loading spinner when the data from the reportStruct is not ready.
 * ErrorBlankState is a component that displays an error that can be gracefully handled in a modal
 * EmptyResult displays a error if the data is empty
 * ChartType reads the munched data and the reportStruct type in order to create and render an specific chart type
 *
 * The Loading component will be displayed by the component as long as the loading flag remains true.
 * Simultaneously, the component will proceed with its execution. If an error occurs,
 * the Chart component will display the ErrorBlankState component; otherwise, it will render the chart.
 *
 * @example
 *
 * const data = {"employee_numbers": 300}
 *
 * const report = new Report({
 *   chart_type: 'numeric_metric',
 *   title: 'My chart'
 * })
 *
 * const reportStruct = new ReportStruct({
 *  resource: report
 *})
 *
 * const reportForm = ReportForm.new({
 *   chart_type: 'string',
 *   initial_data: 'string',
 *   secondary_data: 'string',
 *   tertiary_data: 'string',
 * })
 *
 * <Chart data={data} reportStruct={reportStruct} showLegend={false} reduceMargins={false} loading={false} reportForm={reportForm} error={null} />
 *
 * @constructor
 * @param data - provide the raw data (raw data is data that comes from the backend without any specific format)
 * @param error - provide an error to display instead of rendering a chart
 * @param reportStruct - provide a reportStruct in order to get the chart type and projections
 * @param loading - displays a loading spinner
 * @param showLegend - shows the chart legend
 * @param reportForm - provide reportForm in order to read the projection that the user pick from the UI
 * @param reduceMargins - provide reduceMargins in order to decrease the margin of the chart layer
 */
const Chart = ({
  data,
  error,
  reportStruct,
  loading = false,
  showLegend = true,
  reportForm,
  reduceMargins = false,
  withinDashboardItem,
}: Props) => {
  const type = reportStruct.type
  const ChartType = TYPES[type]!

  if (loading) return <Loading expand />

  // FIXME: Not normal at all. If there's an error it should be detected
  // way back and sent as such, not sneak it past us inside the report content. :(
  const reportError = (data as any)?.message ? (data as any) : error

  if (reportError) {
    return <ErrorBlankSlate error={reportError} />
  }

  if (reportStruct && !reportStruct.canRender) {
    return (
      <ErrorBlankSlate
        error={
          new ChartError(
            `Chart type ${reportStruct.type} needs aggregation`,
            'IncompleteReportConfiguration'
          )
        }
      />
    )
  }

  if (!data?.rows?.length) {
    return <EmptyResult />
  }

  // Chart configuration can be obtained from the report form
  // or the reportStruct (backend request)
  const chartData = reportForm?.data() || reportStruct.sqlConfiguration

  const factory = new MuncherFactory({
    data: data.rows,
    reportStruct,
    chartData,
    chartType: type,
  }).call()

  const munched = factory.call()

  return (
    <ChartType
      munched={munched}
      data={data.rows}
      reportStruct={reportStruct}
      showLegend={showLegend}
      reduceMargins={reduceMargins}
      chartData={chartData}
      withinDashboardItem={withinDashboardItem}
    />
  )
}

export default observer(Chart)
